import React, { FC, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Input,
  Password,
  Label,
  Link,
  Checkbox as CheckboxComponent,
  Flow,
  FormMessage,
  Modal,
  theme,
} from "@xometry/xometry_loft";
import fetch from "cross-fetch";
import {
  loginModal,
  title,
  loginButton,
  content,
  links,
  checkboxWrapper,
  passwordWrapper,
  flowButton,
} from "./CustomerRFQLogin.style";
import { useConfig } from "../../proposals/contexts/configContext";
import MfaMessage from "./MfaMessage";

// This is to fix broken type definitions in the xometry_loft package for Checkbox component
// it is requiring every possible prop to be passed to the component
const Checkbox = CheckboxComponent as any;

export type LoginArgs = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  switchToRegistration?: () => void;
};

const openRegistrationWindow = (getOrigin: string) => {
  const registrationWindow = window.open(
    `${getOrigin}/register?destination=${encodeURIComponent("/quoting/home")}`
  );
  registrationWindow?.focus();
};
export const Login: FC<LoginArgs> = ({
  isOpen,
  onClose,
  onSuccess,
  switchToRegistration = openRegistrationWindow,
}) => {
  const [emailAddress, setEmailAddress] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [mfaRequired, setMfaRequired] = useState(false);
  const rememberMeValue = localStorage.getItem("remember_me_email_address");

  const {
    origins: { www, auth, get },
  } = useConfig();

  const onCloseWrapper = () => {
    setError("");
    setEmailError("");
    setPassword("");
    setPasswordError("");
    onClose();
  };

  const handleEmailAddressChange = (event: { target: { value: string } }) => {
    setEmailAddress(event.target.value);
    setEmailError("");
    setError("");
  };

  const handlePasswordChange = (event: { target: { value: string } }) => {
    setPassword(event.target.value);
    setError("");
    setPasswordError("");
  };

  const handleInputValidation = () => {
    let isValid = true;
    if (!emailAddress) {
      setEmailError("Email is Required");
      isValid = false;
    }
    if (!password) {
      setPasswordError("Password is Required");
      isValid = false;
    }
    return isValid;
  };

  const handleLoginAccount = async () => {
    const isValidInput = handleInputValidation();
    if (!isValidInput) return;

    const staffDomains = [
      "thomasnet.com",
      "xometry.com",
      "xometry.de",
      "xometry.com.tr",
    ];

    const isStaffEmail = staffDomains.includes(emailAddress.split("@")[1]);

    if (isStaffEmail) {
      const url = `${www}/login?domain=www&destination=${window.location.pathname}`;
      window.location.href = url;
      return;
    }

    let createParts = false;
    // check credentials
    try {
      const url = `${auth}/v1/jwt/login`;
      const postData = {
        email_address: emailAddress,
        password,
        no_redirect: true,
      };

      setIsLoading(true);
      const userResponse = await fetch(url, {
        method: "POST",
        credentials: "include",
        headers: {
          "X-Requested-With": "XMLHttpRequest",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(postData),
      });
      setIsLoading(false);

      const response = await userResponse.json();
      let message = response.error_message || response.message;

      if (message === "reset") {
        message = "Invalid email address or password.";
      }
      setError(message);

      if (message === "mfa_required") {
        setMfaRequired(true);
      }

      if (userResponse.ok) {
        createParts = true;
      }
    } catch (e) {
      setError("There was an error logging in, please try again.");
      setIsLoading(false);
    }
    if (createParts) {
      onSuccess();
    }
  };

  const toggleRememberMe = (rememberMe: boolean, email: string) => {
    if (rememberMe) {
      localStorage.setItem("remember_me_email_address", email);
    } else {
      localStorage.removeItem("remember_me_email_address");
    }
  };

  useEffect(() => {
    if (!isOpen) {
      onCloseWrapper();
    }
  }, [isOpen]);

  return (
    <>
      {mfaRequired ? (
        <MfaMessage />
      ) : (
        <div css={content}>
          <div css={title} title="Login">
            Login
          </div>
          <div>
            <Label htmlFor="email">Email Address</Label>
            <Input
              id="email"
              type="text"
              value={emailAddress}
              onChange={handleEmailAddressChange}
              placeholder=""
              invalid={!!emailError}
              autoFocus
              data-testid="emailInput"
            />
            {emailError && (
              <FormMessage style={{ textAlign: "left" }} invalid>
                {emailError}
              </FormMessage>
            )}
            <Label htmlFor="password">Password</Label>
            <div css={passwordWrapper}>
              <Password
                id="password"
                value={password}
                onChange={handlePasswordChange}
                placeholder=""
                invalid={!!passwordError}
                data-testid="passwordInput"
              />
            </div>
            {passwordError && (
              <FormMessage style={{ textAlign: "left" }} invalid>
                {passwordError}
              </FormMessage>
            )}
            <div css={checkboxWrapper}>
              <Checkbox
                id="rememberMe"
                value="rememberMe"
                defaultChecked={!!rememberMeValue}
                onChange={(e: { target: { checked: boolean } }) =>
                  // eslint-disable-next-line prettier/prettier
                  toggleRememberMe(e.target.checked, emailAddress)
                }
              />
              <Label
                style={{ paddingLeft: 0, paddingBottom: "15px" }}
                htmlFor="rememberMe"
                inline
              >
                Remember Me
              </Label>
            </div>
            {error && <Alert kind={theme.kind.danger}>{error}</Alert>}
            <Button
              kind="primary"
              css={loginButton}
              disabled={isLoading}
              onClick={handleLoginAccount}
              scale="large"
            >
              {isLoading ? (
                <Flow width="60px" kind="disabled" css={flowButton} />
              ) : (
                "Log In"
              )}
            </Button>
          </div>
          <div css={links}>
            <Button
              kind="text-blue"
              scale="small"
              onClick={() => switchToRegistration(get)}
            >
              Create a New Account
            </Button>
            <br />
            <Link
              href={`${www}/account/forgot-password`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ paddingLeft: "8px" }}
            >
              Forgot Password
            </Link>
          </div>
        </div>
      )}
    </>
  );
};

export const LoginModal: FC<LoginArgs> = ({ isOpen, onClose, onSuccess }) => {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      css={loginModal}
      overlayProps={{
        style: {
          zIndex: 99999,
          position: "absolute",
          top: 0,
          bottom: 0,
          right: 0,
          left: 0,
          background: "rgba(9, 44, 71, 0.3)",
          height: "200%",
        },
      }}
    >
      <Login isOpen={isOpen} onClose={onClose} onSuccess={onSuccess} />
    </Modal>
  );
};

export default LoginModal;
